import React, { useEffect, useState } from "react";

import { Link, useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Container,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";

import { useForm } from "@mantine/form";

import { IconAlertCircle } from "@tabler/icons-react";

import { Endpoints } from "../../Lib/endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
} from "../../Lib/methods";
import { NonPaginatedResponse } from "../../Lib/responses";
import { AdminProfileProps } from "../../Lib/types";
import { PerformRequest } from "../../Lib/usePerformRequest";

import AnimateOnLoad from "../../Components/AnimateOnLoad";
import MegaLoader from "../../Components/MegaLoader";

import { Colors, LogoSmall, ValidPasswordLength } from "../../Lib/data";

import "./styles.scss";

const CompleteOnboarding: React.FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [isValidating, setValidating] = useState(false);
  const [isTokenValid, setTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [adminProfile, setAdminProfile] = useState<AdminProfileProps | null>(
    null
  );

  const form = useForm({
    initialValues: {
      email: "",
      phone: "",
      name: "",
      password: "",
      confirmPassword: "",
    },
    validate: {
      name: (value) =>
        value.length < 2 ? "Name must have at least 2 letters" : null,
      password: (value) =>
        value.length < ValidPasswordLength
          ? `Password must have at least ${ValidPasswordLength} characters`
          : null,
      confirmPassword: (value, values) =>
        value !== values.password ? "Passwords did not match" : null,
    },
  });

  const ValidateOnboardingToken = async () => {
    setValidating(true);
    const r = await PerformRequest<NonPaginatedResponse<AdminProfileProps>>({
      method: Endpoints.GetValidateOnboardingToken(token as string).method,
      route: Endpoints.GetValidateOnboardingToken(token as string).route,
      callback: () => {
        setValidating(false);
      },
    });
    setValidating(false);

    if (r) {
      if (r.status === 200) {
        setTokenValid(true);
        setAdminProfile(r?.data?.data);
        form.setValues({
          email: r?.data?.data?.email || "",
          phone: r?.data?.data?.phone || "",
          name: r?.data?.data?.name || "",
          password: "",
          confirmPassword: "",
        });
      }
      if (r.status === 401) {
        setTokenValid(false);
        DefaultErrorNotification(
          "Invalid Token",
          "The token is invalid or expired"
        );
      }
      if (r.status === 404) {
        setTokenValid(false);
        DefaultErrorNotification("Invalid Token", "The token does not exist");
      }
    } else {
      DefaultErrorNotification(
        "Invalid Token",
        "The token is invalid or expired"
      );
    }
  };

  const handleSubmit = async (values: typeof form.values) => {
    setIsSubmitting(true);
    try {
      const { name, password } = form.values;
      const response = await PerformRequest({
        method: Endpoints.GetCompleteOnboarding(token as string).method,
        route: Endpoints.GetCompleteOnboarding(token as string).route,
        data: {
          password,
        },
      });

      if (response?.status === 200) {
        DefaultSuccessNotification("Success", "Profile updated successfully");
        setTimeout(() => {
          navigate("/login");
        }, 1000);
        // Redirect to login or dashboard
      } else {
        DefaultErrorNotification("Error", "Failed to update profile");
      }
    } catch (error) {
      DefaultErrorNotification("Error", "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    ValidateOnboardingToken();
  }, []);

  if (isValidating) {
    return (
      <Container size="sm" py="xl" className="complete-onboarding-container">
        <MegaLoader />
      </Container>
    );
  }

  if (!isTokenValid) {
    return (
      <Container size="sm" py="xl" className="complete-onboarding-container">
        <img src={LogoSmall} className="logo" alt="" />
        <Paper
          radius="md"
          p="xl"
          w="100%"
          withBorder
          className="invalid-token-card"
        >
          <Group justify="center" mb="md">
            <ThemeIcon size="xl" radius="xl" color="red" variant="light">
              <IconAlertCircle size={24} />
            </ThemeIcon>
          </Group>
          <Text fw={500} fz={20} ta="center" mb="md" c="red.7">
            Invalid Token
          </Text>
          <Text c="dimmed" ta="center" size="lg" mb="xl">
            The onboarding link you're trying to use is either invalid or has
            expired.
          </Text>
          <Text c="dimmed" ta="center" size="sm">
            Please contact your administrator for a new onboarding link.
          </Text>
          <Link to="/login">
            <Button fullWidth mt="xl" color={Colors.PurplePrimary}>
              Back to Login
            </Button>
          </Link>
        </Paper>
      </Container>
    );
  }

  return (
    <AnimateOnLoad>
      <Container
        className="complete-onboarding-container"
        maw={500}
        w="100%"
        py="xl"
      >
        <img src={LogoSmall} className="logo" alt="" />
        <Paper radius="md" p="xl" w="100%" withBorder>
          <Text ta="center" fw={500} fz={20}>
            Complete Your Profile
          </Text>

          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack>
              <TextInput
                label="Email"
                placeholder="Your email"
                disabled
                {...form.getInputProps("email")}
              />

              <TextInput
                label="Phone"
                placeholder="Your phone number"
                disabled
                {...form.getInputProps("phone")}
              />

              <TextInput
                label="Name"
                placeholder="Your name"
                required
                disabled
                {...form.getInputProps("name")}
              />

              <PasswordInput
                label="Password"
                placeholder="Your password"
                required
                {...form.getInputProps("password")}
              />

              <PasswordInput
                label="Confirm Password"
                placeholder="Confirm your password"
                required
                {...form.getInputProps("confirmPassword")}
              />

              <Button
                type="submit"
                loading={isSubmitting}
                fullWidth
                mt="xl"
                color={Colors.PurplePrimary}
              >
                Complete Onboarding
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </AnimateOnLoad>
  );
};

export default CompleteOnboarding;
